import React, { useEffect, useState, useCallback, useMemo } from "react";
import styled from "styled-components";
import { palette } from "../../../../assets/styles/Palette";
import { H4 } from "../../../../assets/styles/Typography";
import { listSupportInquiries } from "../../../../utils/indexedDB";

const INQUIRY_CREATED_EVENT = "supportInquiry:created";

const MoleculeInquiryList = ({ isLoggedIn }) => {
  const [inquiryList, setInquiryList] = useState([]);
  const [inquiryPage, setInquiryPage] = useState(1);
  const [inquiryPageSize] = useState(5);
  const [inquiryTotal, setInquiryTotal] = useState(0);
  const [hasNextInquiry, setHasNextInquiry] = useState(false);

  const [isLoadingInquiries, setIsLoadingInquiries] = useState(false);
  const [inquiryError, setInquiryError] = useState("");

  // ✅ 상세 모드: 선택한 문의
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  const formatDateTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("ko-KR", { timeZone: "Asia/Seoul" });
  };

  const loadInquiries = useCallback(
    async (page = 1) => {
      if (!isLoggedIn) {
        setInquiryError("로그인이 필요합니다.");
        setInquiryList([]);
        setInquiryTotal(0);
        setInquiryPage(1);
        setHasNextInquiry(false);
        return;
      }

      setIsLoadingInquiries(true);
      setInquiryError("");

      try {
        const data = await listSupportInquiries({ page, size: inquiryPageSize });
        const results = data?.results || data?.inquiries || [];
        const totalCount =
          data?.total_count ||
          data?.count ||
          data?.total ||
          data?.totalCount ||
          results.length ||
          0;

        const hasNext =
          data?.has_next !== undefined
            ? data.has_next
            : results.length >= inquiryPageSize;

        setInquiryList(results);
        setInquiryTotal(totalCount);
        setInquiryPage(page);
        setHasNextInquiry(hasNext);
      } catch (error) {
        setInquiryError("문의 목록을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.");
      } finally {
        setIsLoadingInquiries(false);
      }
    },
    [isLoggedIn, inquiryPageSize]
  );

  // 최초 로딩
  useEffect(() => {
    if (isLoggedIn) loadInquiries(1);
  }, [isLoggedIn, loadInquiries]);

  // ✅ 문의 작성 컴포넌트에서 등록 성공 시, 이벤트 받아서 목록 갱신
  useEffect(() => {
    const onCreated = () => {
      // 상세 보기 중이면 목록으로 복귀 후 갱신하는 게 UX상 안전
      setSelectedInquiry(null);
      loadInquiries(1);
    };
    window.addEventListener(INQUIRY_CREATED_EVENT, onCreated);
    return () => window.removeEventListener(INQUIRY_CREATED_EVENT, onCreated);
  }, [loadInquiries]);

  const totalPages = inquiryTotal
    ? Math.max(1, Math.ceil(inquiryTotal / inquiryPageSize))
    : null;

  const isDetail = Boolean(selectedInquiry);

  const headerTitle = useMemo(() => {
    if (isDetail) return selectedInquiry?.title || "문의 상세";
    return "나의 문의 내역";
  }, [isDetail, selectedInquiry]);

  const contactValue = selectedInquiry?.contact || selectedInquiry?.phone || selectedInquiry?.email || "";

  const handleCardClick = (item) => {
    // ✅ 상세 모드 전환
    setSelectedInquiry(item);
    // 페이지 이동처럼 보이게 스크롤 맨 위(선택)
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackToList = () => {
    setSelectedInquiry(null);
  };

  return (
    <InquirySection>
<InquiryListHeader detail={isDetail}>
  <H4 color="gray800">{headerTitle}</H4>

  {!isDetail ? (
    <span>{inquiryTotal ? `총 ${inquiryTotal}건` : `${inquiryList.length}건`}</span>
  ) : (
    <HeaderMeta>
      <span className="item">
        <span className="label">연락처</span>
        <span className="value">{contactValue || "-"}</span>
      </span>
      <span className="divider" />
      <span className="item">
        <span className="label">문의 일시</span>
        <span className="value">{formatDateTime(selectedInquiry?.inquiryTime)}</span>
      </span>
    </HeaderMeta>
  )}
</InquiryListHeader>


      {inquiryError ? (
        <InlineMessage isError>{inquiryError}</InlineMessage>
      ) : isLoadingInquiries ? (
        <InlineMessage>문의 내역을 불러오는 중입니다...</InlineMessage>
      ) : !isDetail && inquiryList.length === 0 ? (
        <InlineMessage>등록된 문의가 없습니다.</InlineMessage>
      ) : (
        <>
  {/* ✅ 목록 모드: 카드 리스트 */}
  {!isDetail ? (
    <InquiryList>
      {inquiryList.map((item, idx) => {
        const hasReply = Boolean(item?.reply);
        const status = item?.status || (hasReply ? "replied" : "pending");
        const statusLabel = hasReply || status === "replied" ? "답변 완료" : "접수";

        return (
          <InquiryCard
            key={`${item?._id || item?.inquiryId || item?.id || idx}`}
            clickable
            onClick={() => handleCardClick(item)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleCardClick(item);
            }}
          >
            <div className="inquiry-top">
              <strong title={item?.title}>{item?.title}</strong>
              <StatusBadge status={status}>{statusLabel}</StatusBadge>
            </div>

            <div className="inquiry-meta">
              <span>{formatDateTime(item?.inquiryTime)}</span>
            </div>

            <p className="inquiry-content">{item?.content}</p>
          </InquiryCard>
        );
      })}
    </InquiryList>
  ) : (
    /* ✅ 상세 모드: 카드 없이 내용만 */
<DetailBody>
  <DetailContent>{selectedInquiry?.content || "-"}</DetailContent>

  {Boolean(selectedInquiry?.reply) && (
    <DetailReply>
      <div className="reply-header">
        <span>답변</span>
        <span className="reply-time">{formatDateTime(selectedInquiry?.replyTime)}</span>
      </div>
      <p className="reply-text">{selectedInquiry?.reply}</p>
    </DetailReply>
  )}
</DetailBody>

  )}

  {/* ✅ 하단: 목록 = 페이지네이션 / 상세 = 내역 확인하기 */}
  {!isDetail ? (
    <Pagination>
      <button
        type="button"
        onClick={() => loadInquiries(Math.max(1, inquiryPage - 1))}
        disabled={inquiryPage <= 1 || isLoadingInquiries}
      >
        이전
      </button>

      <span>
        {inquiryPage}
        {totalPages ? ` / ${totalPages}` : ""}
      </span>

      <button
        type="button"
        onClick={() => loadInquiries(inquiryPage + 1)}
        disabled={isLoadingInquiries || !hasNextInquiry}
      >
        다음
      </button>
    </Pagination>
  ) : (
    <DetailActions>
      <BackToListButton type="button" onClick={handleBackToList}>
        내역 확인하기
      </BackToListButton>
    </DetailActions>
  )}
</>

      )}
    </InquirySection>
  );
};

export default MoleculeInquiryList;

/* styles */

const InquirySection = styled.div`
  background: ${palette.white};
  border-radius: 12px;
  padding: 24px;
  border: 1px solid ${palette.outlineGray};
`;

const InquiryListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${palette.outlineGray};

  span {
    font-size: 14px;
    color: ${palette.gray600};
    font-family: "Pretendard";
  }
`;

const DetailMeta = styled.div`
  margin: 16px 0 12px;
  padding: 0px;

  .row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    justify-content: flex-start;

    & + .row {
      margin-top: 8px;
    }
  }

  .label {
    font-size: 14px;
    font-weight: 500;
    color: ${palette.gray600};
    font-family: "Pretendard";
    width: 54px;
    text-align: left;
  }

  .value {
    font-size: 13px;
    color: ${palette.gray800};
    text-align: right;
    word-break: break-word;
  }
`;

const InquiryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InquiryCard = styled.div`
  padding: 14px 16px;
  border: 1px solid ${palette.outlineGray};
  border-radius: 10px;
  background: ${palette.white};
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: "Pretendard";

  /* ✅ 목록 모드에서만 카드 높이 제한 */
  height: ${(props) => (props.detail ? "none" : "128px")};

  /* ✅ 클릭 가능 UI */
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  transition: background 0.15s ease;

  &:hover {
    background: ${(props) => (props.clickable ? palette.gray50 : palette.white)};
  }

  .inquiry-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;

    strong {
      font-size: 16px;
      color: ${palette.gray900};
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 100%;
    }
  }

  .inquiry-meta {
    display: flex;
    gap: 10px;
    font-size: 14px;
    color: ${palette.gray500};
  }

  .inquiry-content {
    margin-top: 4px;
    font-size: 14px;
    color: ${palette.gray700};
    line-height: 1.5;
    text-align: left;

    word-break: break-word;

    /* ✅ 목록 모드: 내용이 길면 말줄임(...) */
    ${(props) =>
      !props.detail &&
      `
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2; /* 2줄까지만 보여주고 ... */
      -webkit-box-orient: vertical;
    `}

    /* ✅ 상세 모드: 전체 내용 표시 */
    ${(props) =>
      props.detail &&
      `
      white-space: pre-wrap;
    `}
  }
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  color: ${(props) =>
    props.status === "replied" ? palette.primary : palette.gray700};
  background: ${(props) =>
    props.status === "replied" ? `${palette.primary}14` : palette.gray100};
`;

const ReplyBox = styled.div`
  margin-top: 4px;
  padding: 12px;
  border-radius: 8px;
  background: ${palette.gray50};
  border: 1px solid ${palette.outlineGray};
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;

  .reply-header {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    color: ${palette.gray700};
  }

  .reply-time {
    color: ${palette.gray500};
  }

  p {
    margin: 0;
    font-size: 14px;
    color: ${palette.gray700};
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 24px;

  button {
    padding: 6px 12px;
    border-radius: 6px;
    border: 1px solid ${palette.outlineGray};
    background: ${palette.white};
    cursor: pointer;
    font-size: 14px;
    color: ${palette.gray700};
    transition: all 0.2s ease;
    font-family: "Pretendard";

    &:disabled {
      cursor: not-allowed;
      color: ${palette.gray400};
      background: ${palette.gray50};
    }

    &:hover:not(:disabled) {
      background: ${palette.gray50};
    }
  }

  span {
    font-size: 14px;
    font-family: "Pretendard";
    color: ${palette.gray600};
  }
`;

const DetailActions = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: center;
`;

const BackToListButton = styled.button`
  min-width: 160px;
  padding: 10px 16px;
  border-radius: 10px;
  border: 1px solid ${palette.outlineGray};
  background: ${palette.white};
  font-size: 14px;
  font-weight: 600;
  color: ${palette.gray700};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${palette.gray50};
  }
`;

const InlineMessage = styled.div`
  padding: 40px 12px;
  border-radius: 8px;
  background: ${palette.gray50};
  color: ${palette.gray500};
  font-size: 16px;
  border: 1px solid ${palette.outlineGray};
`;

const DetailBody = styled.div`
  padding: 0px 0 0;
`;

const DetailContent = styled.div`
  font-size: 16px;
  color: ${palette.gray800};
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
  text-align: left;
`;

const DetailReply = styled.div`
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 10px;
  background: ${palette.gray50};
  border: 1px solid ${palette.outlineGray};
  text-align: left;

  .reply-header {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    color: ${palette.gray700};
    margin-bottom: 8px;
  }

  .reply-time {
    color: ${palette.gray500};
  }

  .reply-text {
    margin: 0;
    font-size: 14px;
    color: ${palette.gray800};
    line-height: 1.7;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const DetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-direction: column;

  .label {
    font-size: 14px;
    font-weight: 500;
    color: ${palette.gray600};
    font-family: "Pretendard";
    width: 54px;       /* 기존 DetailMeta 라벨 폭과 통일 */
    text-align: left;
  }

  .value {
    flex: 1;
    min-width: 0;
  }
`;

const HeaderMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: ${palette.gray600};
  white-space: nowrap;

  .item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .label {
    font-weight: 500;
    color: ${palette.gray600};
    font-weight: 400;
  }

  .value {
    font-weight: 500;
    color: ${palette.gray600};
    font-weight: 400;
  }

  .divider {
    width: 1px;
    height: 12px;
    background: ${palette.outlineGray};
  }
`;



